import { Link } from "react-router-dom";
import { Tractor, Wallet, Sun, Wheat, ArrowRight, CheckCircle2 } from "lucide-react";
import { FadeUp } from "@/components/Motion";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/lib/seed-content";

const VALUE_POINTS = [
  {
    icon: Wallet,
    title: "Zero capital outlay",
    body: "SEED owns and maintains the panels. No upfront spend, no maintenance liability.",
  },
  {
    icon: Sun,
    title: "A fixed rate below your current tariff, locked for 25 years",
    body: "Predictable energy costs through commodity cycles.",
  },
  {
    icon: Wheat,
    title: "Your roofs are ideal for solar",
    body: "Cubicle housing, grain stores and packhouses offer large, unobstructed roof areas that turn a major overhead into savings.",
  },
];

export default function Farms() {
  return (
    <div data-testid="farms-page">
      {/* HERO */}
      <section className="relative overflow-hidden navy-panel grain" data-testid="farms-hero">
        <div className="container-seed pt-20 pb-24 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <FadeUp>
                <div className="text-overline text-emerald-400">Sector · Farms</div>
              </FadeUp>
              <FadeUp delay={1}>
                <h1 className="mt-4 font-display font-extrabold text-white tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                  Built for farms<br />that <span className="text-emerald-400">never switch off</span>.
                </h1>
              </FadeUp>
              <FadeUp delay={2}>
                <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
                  Dairy, poultry, arable and mixed farms run milking, refrigeration, grain drying and cold
                  storage around the clock — making electricity one of the largest and least predictable costs
                  on the farm. SEED puts solar on your roofs at zero capital cost, and you pay a fixed rate
                  below your current tariff for 25 years.
                </p>
              </FadeUp>
              <FadeUp delay={3}>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/contact"
                    data-testid="farms-cta-book"
                    className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold py-4 px-6 rounded-lg group"
                  >
                    Book a 15-minute call
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
                  </Link>
                  <Link
                    to="/trustee-hub"
                    className="inline-flex items-center justify-center bg-white/5 hover:bg-white/10 text-white border border-white/15 font-semibold py-4 px-6 rounded-lg"
                  >
                    Trustee Hub
                  </Link>
                </div>
              </FadeUp>
            </div>
            <FadeUp delay={2} className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src={IMAGES.commercialRoof}
                  alt="Large agricultural rooftop with solar panels"
                  className="w-full h-[420px] object-cover"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* VALUE POINTS */}
      <section className="container-seed section-pad" data-testid="farms-value">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-overline">For the farm</div>
            <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Turn a major overhead into <span className="text-emerald-600">savings</span>.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
              Electricity is one of the few farm inputs you cannot switch off. SEED fixes the price of it —
              at a rate below what you're paying today, for the next quarter century.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-4">
              {VALUE_POINTS.map((v, i) => (
                <FadeUp key={v.title} delay={i}>
                  <li className="flex gap-4 bg-white border border-slate-200 rounded-xl p-6">
                    <div className="w-11 h-11 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-none">
                      <v.icon className="w-5 h-5 text-emerald-600" strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-slate-900 text-lg leading-snug">{v.title}</h3>
                      <p className="mt-2 text-slate-600 leading-relaxed text-sm">{v.body}</p>
                    </div>
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <CTASection
        overline="Farm Programme"
        title="Make your roofs work as hard as you do."
        subtitle="Send us a recent bill and a brief description of your farm buildings. We'll respond with a clear, plain-English assessment."
        ctaLabel="Book a 15-minute call"
        testId="farms-cta-section"
      />
    </div>
  );
}
